import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Bot, Plus, Trash2, Mail, ShieldOff, Globe2, Search, ExternalLink, CheckCircle2,
  Clock, Send, Eye, AlertTriangle, Sparkles, BarChart3, Settings, Pause, Play, Save,
  Pencil, X, History, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { toast } from 'sonner';

// Natural Earth countries TopoJSON (≈ 110m resolution, 100 kb gzip, cached on jsdelivr CDN)
const WORLD_GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country → language hint (purely for the badge in the table — the backend re-derives it)
const COUNTRY_LANG = {
  france: 'fr', belgique: 'fr', suisse: 'fr', luxembourg: 'fr', monaco: 'fr',
  maroc: 'fr', tunisie: 'fr', 'algérie': 'fr', algerie: 'fr', 'sénégal': 'fr', senegal: 'fr',
  'côte d\'ivoire': 'fr', canada: 'fr', 'québec': 'fr',
  allemagne: 'de', germany: 'de', autriche: 'de', austria: 'de',
  italie: 'it', italy: 'it',
  espagne: 'es', spain: 'es', mexique: 'es', argentine: 'es',
};
const POPULAR_COUNTRIES = [
  'France', 'Belgique', 'Suisse', 'Luxembourg', 'Allemagne', 'Italie', 'Espagne',
  'Royaume-Uni', 'Pays-Bas', 'Pologne', 'Portugal', 'Maroc', 'Tunisie', 'Canada',
  'États-Unis', 'Mexique', 'Brésil', 'Autre',
];
const LANG_LABEL = { fr: '🇫🇷 FR', en: '🇬🇧 EN', de: '🇩🇪 DE', it: '🇮🇹 IT', es: '🇪🇸 ES' };

const detectLang = (country, override) => {
  if (override) return override;
  if (!country) return 'en';
  return COUNTRY_LANG[country.trim().toLowerCase()] || 'en';
};

const STATUS_LABEL = {
  nouveau: { label: 'Nouveau', color: 'text-zinc-400 bg-zinc-800' },
  qualifie: { label: 'Qualifié', color: 'text-sky-300 bg-sky-500/10' },
  contacte: { label: 'Contacté', color: 'text-amber-300 bg-amber-500/10' },
  relance_1: { label: 'Relance 1', color: 'text-amber-300 bg-amber-500/10' },
  relance_2: { label: 'Relance 2', color: 'text-orange-300 bg-orange-500/10' },
  relance_3: { label: 'Relance 3', color: 'text-orange-400 bg-orange-500/15' },
  dormant: { label: 'En sommeil', color: 'text-violet-300 bg-violet-500/10' },
  reveil: { label: 'Réveil', color: 'text-fuchsia-300 bg-fuchsia-500/10' },
  reveil_1: { label: 'Réveil R1', color: 'text-fuchsia-300 bg-fuchsia-500/10' },
  reveil_2: { label: 'Réveil R2', color: 'text-fuchsia-400 bg-fuchsia-500/15' },
  interesse: { label: 'Intéressé', color: 'text-emerald-300 bg-emerald-500/10' },
  appel_a_prevoir: { label: 'Appel à prévoir', color: 'text-emerald-300 bg-emerald-500/10' },
  client: { label: 'Client', color: 'text-emerald-400 bg-emerald-500/15' },
  partenaire_valide: { label: 'Partenaire validé', color: 'text-emerald-400 bg-emerald-500/15' },
  refuse: { label: 'Refusé', color: 'text-zinc-500 bg-zinc-800' },
  email_invalide: { label: 'Email invalide', color: 'text-rose-300 bg-rose-500/15 border border-rose-500/30' },
  archive_silencieux: { label: 'Archivé', color: 'text-zinc-500 bg-zinc-800/60' },
  // Legacy fallbacks (older docs)
  relance: { label: 'Relancé', color: 'text-violet-300 bg-violet-500/10' },
  repondu: { label: 'A répondu', color: 'text-emerald-300 bg-emerald-500/10' },
  desinscrit: { label: 'Désinscrit', color: 'text-rose-300 bg-rose-500/10' },
  archive: { label: 'Archivé', color: 'text-zinc-500 bg-zinc-800/40' },
};

// Quick-filter chips shown above the prospects table.
const STATUS_FILTERS = [
  { id: 'all',           label: 'Tous',           statuses: null },
  { id: 'active',        label: 'Actifs',         statuses: ['nouveau','qualifie','contacte','relance_1','relance_2','relance_3'] },
  { id: 'reveil',        label: 'En réveil',      statuses: ['reveil','reveil_1','reveil_2'] },
  { id: 'dormant',       label: 'En sommeil',     statuses: ['dormant'] },
  { id: 'interested',    label: 'Intéressés',     statuses: ['interesse','appel_a_prevoir','client','partenaire_valide'] },
  { id: 'email_invalide',label: 'Email invalide', statuses: ['email_invalide'] },
  { id: 'archive',       label: 'Archivés',       statuses: ['archive_silencieux','refuse','archive','desinscrit'] },
];

export default function AiAgentSimplePage() {
  const [tab, setTab] = useState('prospects');
  const [companies, setCompanies] = useState([]);
  const [emails, setEmails] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [state, setState] = useState(null);
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [emailView, setEmailView] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [perfDays, setPerfDays] = useState(30);
  const [mapData, setMapData] = useState([]);
  const [detailProspect, setDetailProspect] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  // Manual "Lancer maintenant" — fires an immediate cycle. Useful when the
  // admin doesn't want to wait the 10-min scheduler interval (e.g. just added
  // a batch of prospects). The button is rendered in the AutoModeActivityPanel.
  const handleRunNow = useCallback(async () => {
    if (runningNow) return;
    setRunningNow(true);
    try {
      // Use the daily-quota remaining as the cap so we drain as much as possible.
      const remaining = Math.max(1, (state?.daily_email_limit || 40) - (state?.sent_today || 0));
      const cap = Math.min(remaining, 40);
      const { data } = await api.post('/ai-agent/agent/run', {
        max_actions: cap,
        send: true,
        include_first_contact: null,
      });
      const sent = data?.sent_count || 0;
      const skipped = data?.skipped_count || 0;
      toast.success(`Cycle terminé : ${sent} envoyé(s), ${skipped} ignoré(s).`, { duration: 6000 });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (status === 409 ? 'Un cycle est déjà en cours.' : 'Erreur lors du lancement');
      toast.error(`${msg}${status ? ` (HTTP ${status})` : ''}`, { duration: 7000 });
    } finally {
      setRunningNow(false);
    }
  }, [runningNow, state?.daily_email_limit, state?.sent_today]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/companies', { params: { limit: 1000 } });
      setCompanies(data);
    } catch (e) {
      // Surface the real backend error so the admin can diagnose production
      // issues at a glance instead of staring at a generic toast.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailStr = typeof detail === 'string'
        ? detail
        : (detail ? JSON.stringify(detail).slice(0, 160) : '');
      const msg = status
        ? `Erreur chargement prospects (HTTP ${status}${detailStr ? ' — ' + detailStr : ''})`
        : `Erreur chargement prospects (${e?.message || 'réseau'})`;
      toast.error(msg, { duration: 8000 });
    }
  }, []);

  const [emailSearch, setEmailSearch] = useState('');
  const [emailLimit, setEmailLimit] = useState(50);

  const fetchEmails = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/sent-emails', {
        params: {
          limit: emailLimit,
          search: emailSearch.trim() || undefined,
          since_days: emailSearch.trim() ? undefined : 90,  // search bypasses time cutoff
        },
      });
      setEmails(data);
    } catch (e) { /* silent */ }
  }, [emailSearch, emailLimit]);

  const fetchBlacklist = useCallback(async () => {
    try {
      const { data } = await api.get('/blacklist');
      setBlacklist(data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/agent/state');
      setState(data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchDashStats = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/dashboard/performance', { params: { days: perfDays } });
      setDashStats(data);
    } catch (e) { /* silent */ }
  }, [perfDays]);

  const fetchMapData = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-agent/map-data');
      setMapData(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchCompanies(); fetchEmails(); fetchBlacklist(); fetchState(); fetchDashStats(); fetchMapData(); }, [fetchCompanies, fetchEmails, fetchBlacklist, fetchState, fetchDashStats, fetchMapData]);

  // Poll the agent state every 5s so the animated "Mode Auto" indicator stays in sync with reality.
  // We always poll (cheap GET, admin-only page) — the indicator itself is gated by mode === 'auto'.
  useEffect(() => {
    const id = setInterval(fetchState, 5000);
    return () => clearInterval(id);
  }, [fetchState]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce prospect ?')) return;
    try {
      await api.delete(`/ai-agent/companies/${id}`);
      toast.success('Prospect supprimé');
      fetchCompanies();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const handleBlacklist = async (email) => {
    if (!email) return;
    if (!window.confirm(`Blacklister ${email} ?\nIl ne sera plus jamais contacté par l'agent IA.`)) return;
    try {
      await api.post('/blacklist', { email, reason: 'Blacklisté depuis le dashboard' });
      toast.success('Email blacklisté');
      fetchBlacklist();
      fetchCompanies();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleUnblacklist = async (id) => {
    if (!window.confirm('Retirer cette adresse de la blacklist ?')) return;
    try {
      await api.delete(`/blacklist/${id}`);
      toast.success('Retiré de la blacklist');
      fetchBlacklist();
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAllVisible = (visibleIds, currentlyAllSelected) => {
    setSelectedIds(currentlyAllSelected ? [] : visibleIds);
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.length} prospect(s) sélectionné(s) ?`)) return;
    try {
      await api.post('/ai-agent/companies/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} prospect(s) supprimé(s)`);
      setSelectedIds([]);
      fetchCompanies();
    } catch (e) { toast.error('Erreur'); }
  };

  const bulkBlacklist = async () => {
    if (selectedIds.length === 0) return;
    const targets = companies.filter(c => selectedIds.includes(c.id) && c.email);
    if (targets.length === 0) { toast.error('Aucun email valide sélectionné'); return; }
    if (!window.confirm(`Blacklister ${targets.length} email(s) ? Ils ne seront plus jamais contactés.`)) return;
    try {
      for (const t of targets) {
        await api.post('/blacklist', { email: t.email, reason: 'Bulk blacklist' });
      }
      toast.success(`${targets.length} blacklisté(s)`);
      setSelectedIds([]);
      fetchBlacklist();
      fetchCompanies();
    } catch (e) { toast.error('Erreur partielle'); }
  };

  const bulkStatus = async (newStatus) => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/ai-agent/companies/bulk-status', { ids: selectedIds, status: newStatus });
      toast.success(`${selectedIds.length} prospect(s) marqués "${newStatus}"`);
      setSelectedIds([]);
      fetchCompanies();
    } catch (e) { toast.error('Erreur'); }
  };

  const [statusFilter, setStatusFilter] = useState('all');

  const filteredCompanies = companies.filter(c => {
    // Status filter chip
    const f = STATUS_FILTERS.find(x => x.id === statusFilter);
    if (f && f.statuses && !f.statuses.includes(c.status)) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.country || '').toLowerCase().includes(q)
      || (c.specialty || '').toLowerCase().includes(q);
  });

  // Per-filter counts for the chip badges
  const filterCounts = companies.reduce((acc, c) => {
    STATUS_FILTERS.forEach(f => {
      if (!f.statuses || f.statuses.includes(c.status)) acc[f.id] = (acc[f.id] || 0) + 1;
    });
    return acc;
  }, {});

  // Stats for header
  const totalWithEmail = companies.filter(c => c.email && c.status !== 'desinscrit' && c.status !== 'archive').length;
  const contacted = companies.filter(c => (c.contact_count || 0) > 0).length;
  const responded = companies.filter(c => c.status === 'repondu').length;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ai-agent-simple-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight flex items-center flex-wrap gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400 shrink-0" /> Agent IA — Démarchage
            {state?.mode === 'auto' && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold"
                data-testid="header-auto-live-pill"
                title="L'agent IA tourne en mode automatique"
              >
                <span className="agent-radar" aria-hidden="true">
                  <span className="ring"></span>
                  <span className="ring delay"></span>
                  <span className="dot"></span>
                </span>
                Auto · Live
              </span>
            )}
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-1">
            Saisie manuelle des prospects. L'IA écrit et envoie les emails dans la bonne langue selon le pays.
          </p>
        </div>
        <Button data-testid="new-prospect-btn" onClick={() => setNewOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium w-full sm:w-auto shrink-0">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un prospect
        </Button>
      </div>

      {/* Compact stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Prospects contactables" value={totalWithEmail} icon={Mail} color="text-emerald-400" testId="stat-contactable" />
        <StatCard label="Déjà contactés" value={contacted} icon={Send} color="text-amber-400" testId="stat-contacted" />
        <StatCard label="A répondu" value={responded} icon={CheckCircle2} color="text-violet-400" testId="stat-responded" />
        <StatCard label="Blacklist" value={blacklist.length} icon={ShieldOff} color="text-rose-400" testId="stat-blacklist" />
      </div>

      {state?.mode === 'auto' && (
        <AutoModeActivityPanel state={state} onRunNow={handleRunNow} runningNow={runningNow} />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-400 text-zinc-400 rounded-sm">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="prospects" data-testid="tab-prospects" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-400 text-zinc-400 rounded-sm">
            Prospects ({companies.length})
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-400 text-zinc-400 rounded-sm">
            Emails envoyés ({emails.length})
          </TabsTrigger>
          <TabsTrigger value="blacklist" data-testid="tab-blacklist" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-rose-400 text-zinc-400 rounded-sm">
            Blacklist ({blacklist.length})
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-200 text-zinc-400 rounded-sm">
            <Settings className="w-3.5 h-3.5 mr-1.5" /> Paramètres
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardTab stats={dashStats} perfDays={perfDays} setPerfDays={setPerfDays} companies={companies} emails={emails} mapData={mapData} />
        </TabsContent>

        <TabsContent value="prospects" className="mt-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="prospects-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, pays ou spécialité…"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-9 h-9 text-sm"
            />
          </div>
          {/* Status filter chips */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="status-filter-bar">
            {STATUS_FILTERS.map(f => {
              const active = statusFilter === f.id;
              const count = filterCounts[f.id] || 0;
              return (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  data-testid={`status-chip-${f.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${
                    active
                      ? 'bg-amber-500 text-zinc-950 border-amber-400 font-semibold'
                      : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 text-[10px] ${active ? 'text-zinc-700' : 'text-zinc-500'}`}>{count}</span>
                </button>
              );
            })}
          </div>
          {selectedIds.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2 flex items-center justify-between" data-testid="bulk-toolbar">
              <span className="text-xs text-amber-300 font-medium">{selectedIds.length} sélectionné(s)</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => bulkStatus('archive')} className="h-7 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm" data-testid="bulk-archive-btn">
                  Archiver
                </Button>
                <Button size="sm" variant="outline" onClick={bulkBlacklist} className="h-7 text-xs border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-sm" data-testid="bulk-blacklist-btn">
                  <ShieldOff className="w-3 h-3 mr-1" /> Blacklister
                </Button>
                <Button size="sm" variant="outline" onClick={bulkDelete} className="h-7 text-xs border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-sm" data-testid="bulk-delete-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                </Button>
                <button onClick={() => setSelectedIds([])} className="text-xs text-zinc-500 hover:text-zinc-200 ml-1">Effacer</button>
              </div>
            </div>
          )}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm overflow-hidden">
            {filteredCompanies.length === 0 ? (
              <CardContent className="text-center py-16 text-zinc-500">
                {search ? 'Aucun prospect ne correspond à la recherche' : 'Aucun prospect. Cliquez sur "Ajouter un prospect" pour commencer.'}
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-zinc-800">
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="text-left p-3 w-8">
                        <Checkbox
                          data-testid="select-all-prospects"
                          checked={filteredCompanies.length > 0 && filteredCompanies.every(c => selectedIds.includes(c.id))}
                          onCheckedChange={() => selectAllVisible(filteredCompanies.map(c => c.id), filteredCompanies.every(c => selectedIds.includes(c.id)))}
                        />
                      </th>
                      <th className="text-left p-3">Entreprise</th>
                      <th className="text-left p-3">Spécialité</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Pays</th>
                      <th className="text-left p-3">Langue</th>
                      <th className="text-left p-3">Statut</th>
                      <th className="text-center p-3">Contacts</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map(c => {
                      const lang = detectLang(c.country, c.language);
                      const st = STATUS_LABEL[c.status] || STATUS_LABEL.nouveau;
                      const isSelected = selectedIds.includes(c.id);
                      return (
                        <tr
                          key={c.id}
                          className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors ${isSelected ? 'bg-amber-500/5' : ''}`}
                          data-testid={`prospect-row-${c.id}`}
                          onClick={(e) => {
                            // Don't open dialog when clicking a checkbox or action button
                            if (e.target.closest('button, [role="checkbox"], input')) return;
                            setDetailProspect(c);
                          }}
                        >
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(c.id)}
                              data-testid={`select-${c.id}`}
                            />
                          </td>
                          <td className="p-3 text-zinc-200 font-medium">
                            <span className="hover:text-amber-300">{c.name}</span>
                          </td>
                          <td className="p-3 text-zinc-400 text-xs max-w-[180px] truncate" title={c.specialty || ''}>
                            {c.specialty
                              ? <span className="text-zinc-300">{c.specialty}</span>
                              : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="p-3 text-zinc-400 text-xs">{c.email || <span className="text-rose-400">—</span>}</td>
                          <td className="p-3 text-zinc-400 text-xs">{c.country || '—'}</td>
                          <td className="p-3 text-xs">{LANG_LABEL[lang] || lang.toUpperCase()}</td>
                          <td className="p-3"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${st.color}`}>{st.label}</span></td>
                          <td className="p-3 text-center text-zinc-400 text-xs">{c.contact_count || 0}</td>
                          <td className="p-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); setDetailProspect(c); }}
                                title="Voir / Modifier"
                                className="p-1 text-zinc-500 hover:text-amber-400"
                                data-testid={`view-${c.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              {c.email && c.status !== 'desinscrit' && (
                                <button onClick={(e) => { e.stopPropagation(); handleBlacklist(c.email); }} title="Blacklister" className="p-1 text-zinc-500 hover:text-rose-400" data-testid={`blacklist-${c.id}`}>
                                  <ShieldOff className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} title="Supprimer" className="p-1 text-zinc-500 hover:text-rose-400" data-testid={`delete-${c.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="emails" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="emails-search"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="Rechercher par nom de prospect, email, ou objet…"
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-9 h-9 text-sm"
              />
            </div>
            <span className="text-[10px] text-zinc-500">
              {emailSearch.trim()
                ? `Recherche dans tout l'historique`
                : `${emails.length} email(s) — 90 derniers jours`}
            </span>
          </div>
          {emails.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="text-center py-16 text-zinc-500">
                {emailSearch.trim() ? 'Aucun email ne correspond à votre recherche.' : 'Aucun email envoyé dans les 90 derniers jours.'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {emails.map(e => {
                const isDraft = e.type === 'email_draft' || e.type === 'auto_email';
                const isManual = e.type === 'email_sent_manual';
                const isReal = e.type === 'email_sent_real' || e.type === 'email_sent';
                return (
                <Card key={e.id || `${e.company_id}-${e.created_at}`} className="bg-zinc-900 border-zinc-800 rounded-sm hover:border-zinc-700 cursor-pointer" onClick={() => setEmailView(e)} data-testid={`email-row-${e.id || e.created_at}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Mail className={`w-4 h-4 shrink-0 ${isDraft ? 'text-amber-400' : 'text-emerald-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-zinc-200 truncate">{e.subject || '(sans objet)'}</p>
                          {isReal && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold uppercase tracking-wider" data-testid="sent-badge">
                              ✓ Envoyé · Auto
                            </span>
                          )}
                          {isManual && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-500/15 text-sky-300 border border-sky-500/30 font-semibold uppercase tracking-wider">
                              ✓ Envoyé · Manuel
                            </span>
                          )}
                          {isDraft && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold uppercase tracking-wider">
                              Brouillon
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 truncate">
                          → {e.company_name || '—'} · {e.company_email || ''} {e.company_country && <>· {e.company_country}</>}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-zinc-600">{new Date(e.created_at).toLocaleString('fr-FR')}</p>
                        {e.company_status === 'repondu' && <span className="text-[10px] text-emerald-400">✓ A répondu</span>}
                      </div>
                      <Eye className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
          {!emailSearch.trim() && emails.length >= emailLimit && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={() => setEmailLimit(l => l + 50)}
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-sm text-xs"
                data-testid="load-more-emails-btn"
              >
                Charger 50 emails plus anciens
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="blacklist" className="mt-4 space-y-3">
          <BlacklistTabContent
            blacklist={blacklist}
            onUnblacklist={handleUnblacklist}
            onRefresh={fetchBlacklist}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <SettingsTab state={state} onSaved={fetchState} />
        </TabsContent>
      </Tabs>

      {newOpen && <ProspectFormDialog open={newOpen} onOpenChange={setNewOpen} onSaved={fetchCompanies} />}
      {emailView && <EmailViewDialog email={emailView} onClose={() => setEmailView(null)} />}
      {detailProspect && (
        <ProspectDetailDialog
          prospect={detailProspect}
          onClose={() => setDetailProspect(null)}
          onSaved={() => { fetchCompanies(); }}
          onDeleted={() => { setDetailProspect(null); fetchCompanies(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }}>{value}</p>
    </div>
  );
}

function ProspectFormDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', country: 'France', language: '', specialty: '', contact_name: '', notes: '', is_existing_client: false });
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null); // {matched_on, existing} when duplicate found
  const [checking, setChecking] = useState(false);

  const detectedLang = detectLang(form.country, form.language);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setForm({ name: '', email: '', country: 'France', language: '', specialty: '', contact_name: '', notes: '', is_existing_client: false });
      setDup(null);
      setChecking(false);
    }
  }, [open]);

  // Live duplicate check (debounced 400ms) — fires whenever name or email changes
  useEffect(() => {
    if (!open) return;
    const name = form.name.trim();
    const email = form.email.trim();
    // Need at least one usable input — name ≥ 2 chars OR a fully-typed email
    const hasName = name.length >= 2;
    const hasEmail = email.includes('@') && email.split('@')[1]?.includes('.');
    if (!hasName && !hasEmail) {
      setDup(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (hasName) params.name = name;
        if (hasEmail) params.email = email;
        const { data } = await api.get('/ai-agent/companies/check-duplicate', { params });
        setDup(data?.duplicate ? data : null);
      } catch (_e) {
        setDup(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => { clearTimeout(t); setChecking(false); };
  }, [form.name, form.email, open]);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Nom et email requis');
      return;
    }
    if (dup) {
      toast.error(`Doublon détecté — ce prospect existe déjà (${dup.matched_on}).`);
      return;
    }
    setSaving(true);
    try {
      await api.post('/ai-agent/companies', form);
      toast.success('Prospect ajouté');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      // 409 Conflict — duplicate. Backend returns: { message, matched_on, existing: {...} }
      if (e?.response?.status === 409 && raw && typeof raw === 'object' && raw.existing) {
        const ex = raw.existing;
        setDup({ matched_on: raw.matched_on, existing: ex });
        toast.error(
          `Doublon : ${ex.name} ${ex.email ? `(${ex.email})` : ''} existe déjà — statut "${ex.status}".`,
          { duration: 6500 }
        );
      } else if (typeof raw === 'string') {
        toast.error(raw);
      } else if (Array.isArray(raw)) {
        toast.error(raw.map(x => x?.msg || JSON.stringify(x)).join(' · '));
      } else {
        toast.error('Erreur');
      }
    } finally { setSaving(false); }
  };

  const dupNameMatch = dup?.matched_on === 'name';
  const dupEmailMatch = dup?.matched_on === 'email';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="prospect-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <Sparkles className="w-5 h-5 text-amber-400" /> Nouveau prospect
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            L'IA détectera automatiquement la langue d'écriture selon le pays.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Nom de l'entreprise *</Label>
            <Input
              data-testid="form-name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className={`bg-zinc-950 text-zinc-100 rounded-sm mt-1 ${dupNameMatch ? 'border-rose-500 focus-visible:ring-rose-500' : 'border-zinc-800'}`}
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email *</Label>
            <Input
              data-testid="form-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              className={`bg-zinc-950 text-zinc-100 rounded-sm mt-1 ${dupEmailMatch ? 'border-rose-500 focus-visible:ring-rose-500' : 'border-zinc-800'}`}
              placeholder="contact@entreprise.com"
            />
          </div>
          {dup && dup.existing && (
            <div
              className="bg-rose-500/10 border border-rose-500/40 rounded-sm p-3 text-xs flex items-start gap-2"
              data-testid="duplicate-warning"
            >
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-rose-200 font-medium">
                  Doublon détecté sur le {dup.matched_on === 'email' ? 'email' : 'nom'}
                </p>
                <p className="text-rose-300/80 mt-0.5">
                  <span className="font-medium text-rose-100">{dup.existing.name}</span>
                  {dup.existing.email && <span className="text-rose-300/70"> · {dup.existing.email}</span>}
                  {dup.existing.country && <span className="text-rose-300/70"> · {dup.existing.country}</span>}
                </p>
                <p className="text-rose-400/70 mt-1">
                  Statut actuel : <span className="font-medium">{dup.existing.status || '—'}</span>
                  {typeof dup.existing.contact_count === 'number' && (
                    <span className="ml-2">· {dup.existing.contact_count} contact(s)</span>
                  )}
                </p>
              </div>
            </div>
          )}
          {!dup && checking && (form.name.trim().length >= 2 || form.email.includes('@')) && (
            <p className="text-[11px] text-zinc-500 italic" data-testid="duplicate-checking">Vérification du doublon…</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Pays *</Label>
              <Select value={form.country} onValueChange={(v) => setForm(f => ({ ...f, country: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="form-country"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-60">
                  {POPULAR_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Langue email (auto)</Label>
              <Select value={form.language || 'auto'} onValueChange={(v) => setForm(f => ({ ...f, language: v === 'auto' ? '' : v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="form-language"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                  <SelectItem value="auto">Auto ({LANG_LABEL[detectedLang]})</SelectItem>
                  <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                  <SelectItem value="es">🇪🇸 Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Spécialité / Domaine <span className="text-zinc-600">(optionnel)</span></Label>
            <Input data-testid="form-specialty" value={form.specialty} onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" placeholder="Ex: Usinage CNC, tournage…" />
          </div>
          <label className="flex items-start gap-3 p-3 rounded-sm border border-zinc-800 bg-zinc-950/50 hover:border-amber-500/40 cursor-pointer transition-colors" data-testid="form-existing-client-row">
            <Checkbox
              checked={!!form.is_existing_client}
              onCheckedChange={(v) => setForm(f => ({ ...f, is_existing_client: !!v }))}
              className="mt-0.5 border-zinc-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="form-existing-client"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-zinc-200">Déjà client</div>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Coché → le 1ᵉʳ mail sera rédigé en mode chaleureux (remerciement pour la précédente
                collaboration + proposition de capacité d'usinage disponible). Pas de présentation,
                pas d'argumentaire long.
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">Annuler</Button>
          <Button
            onClick={save}
            disabled={saving || !!dup}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-prospect-btn"
          >
            {saving ? 'Enregistrement…' : dup ? 'Doublon — bloqué' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailViewDialog({ email, onClose }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-view-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>
            {email.subject || '(sans objet)'}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            → {email.company_name || '—'} · {email.company_email || ''} {email.company_country && <>· {email.company_country}</>}
            <br />
            {new Date(email.created_at).toLocaleString('fr-FR')}
            {(email.type === 'email_draft' || email.type === 'auto_email') && <span className="ml-2 text-amber-400">[Brouillon — non envoyé]</span>}
            {(email.type === 'email_sent_real' || email.type === 'email_sent') && <span className="ml-2 text-emerald-400">[✓ Envoyé via Resend (Auto)]</span>}
            {email.type === 'email_sent_manual' && <span className="ml-2 text-sky-400">[✓ Envoyé manuellement]</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 whitespace-pre-line text-sm text-zinc-200 font-sans">
          {email.body || '(Corps de l\'email non disponible)'}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProspectDetailDialog({ prospect, onClose, onSaved, onDeleted }) {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [openReplyLog, setOpenReplyLog] = useState(null);
  const [form, setForm] = useState({
    name: prospect.name || '',
    email: prospect.email || '',
    country: prospect.country || 'France',
    language: prospect.language || '',
    specialty: prospect.specialty || '',
    contact_name: prospect.contact_name || '',
    website: prospect.website || '',
    phone: prospect.phone || '',
    notes: prospect.notes || '',
  });

  useEffect(() => {
    (async () => {
      setLogsLoading(true);
      try {
        const { data } = await api.get(`/ai-agent/companies/${prospect.id}/logs`);
        setLogs(data);
      } catch (e) { /* silent */ }
      finally { setLogsLoading(false); }
    })();
  }, [prospect.id]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      // Strip empty email — Pydantic v2 EmailStr rejects '' (would return 422)
      const payload = { ...form, email: (form.email || '').trim() || null };
      await api.put(`/ai-agent/companies/${prospect.id}`, payload);
      toast.success('Prospect modifié');
      onSaved?.();
      setEdit(false);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = 'Erreur';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      else if (raw && typeof raw === 'object') msg = raw.message || JSON.stringify(raw);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement « ${prospect.name} » ?`)) return;
    try {
      await api.delete(`/ai-agent/companies/${prospect.id}`);
      toast.success('Prospect supprimé');
      onDeleted?.();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const detectedLang = detectLang(form.country, form.language);
  const st = STATUS_LABEL[prospect.status] || STATUS_LABEL.nouveau;

  const LOG_LABEL = {
    email_sent: { label: 'Email envoyé', color: 'text-emerald-300', icon: Send },
    email_draft: { label: 'Brouillon généré', color: 'text-amber-300', icon: Mail },
    status_change: { label: 'Changement de statut', color: 'text-sky-300', icon: AlertTriangle },
    reply_received: { label: 'Réponse reçue', color: 'text-violet-300', icon: Mail },
    unsubscribe: { label: 'Désinscription', color: 'text-rose-300', icon: ShieldOff },
    gmail_positive_reply: { label: '🟢 Réponse intéressé', color: 'text-emerald-300', icon: Mail },
    gmail_negative_reply: { label: '❌ Refus', color: 'text-rose-300', icon: Mail },
    gmail_received_reply: { label: '📩 Réponse reçue', color: 'text-sky-300', icon: Mail },
  };
  const REPLY_TYPES = new Set(['gmail_positive_reply', 'gmail_negative_reply', 'gmail_received_reply']);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="prospect-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-xl font-bold flex items-center gap-2 truncate" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <Bot className="w-5 h-5 text-amber-400 shrink-0" /> {prospect.name}
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs mt-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${st.color} mr-2`}>{st.label}</span>
                {prospect.contact_count || 0} contact(s)
                {prospect.last_contacted_at && <> · Dernier contact : {new Date(prospect.last_contacted_at).toLocaleDateString('fr-FR')}</>}
              </DialogDescription>
            </div>
            {!edit && (
              <Button
                onClick={() => setEdit(true)}
                size="sm"
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium shrink-0"
                data-testid="edit-prospect-btn"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
              </Button>
            )}
          </div>
        </DialogHeader>

        {!edit ? (
          <div className="space-y-4 py-2">
            {/* Email invalid alert banner */}
            {prospect.status === 'email_invalide' && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-sm px-3 py-2 text-xs text-rose-200 flex items-start gap-2" data-testid="email-invalide-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-300" />
                <div>
                  <strong className="text-rose-100">Email isolé — l'agent ne contactera plus ce prospect</strong>
                  <p className="mt-0.5">
                    {prospect.email_failures ? `${prospect.email_failures} échec(s) d'envoi consécutifs.` : 'Bounce détecté.'}
                    {prospect.last_email_error && <span className="block mt-0.5 text-rose-300/80 italic">« {prospect.last_email_error.slice(0, 200)} »</span>}
                  </p>
                  <p className="mt-1 text-rose-300/70">Corrigez l'email puis remettez le statut sur « Nouveau » pour réessayer.</p>
                </div>
              </div>
            )}
            {/* Dormant info banner */}
            {prospect.status === 'dormant' && (
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-sm px-3 py-2 text-xs text-violet-200 flex items-start gap-2" data-testid="dormant-banner">
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-violet-300" />
                <div>
                  <strong className="text-violet-100">En sommeil — réveil automatique programmé</strong>
                  <p className="mt-0.5">3 relances envoyées sans réponse. L'agent réveillera ce prospect dans quelques semaines avec une nouvelle accroche pour ne pas spammer.</p>
                </div>
              </div>
            )}
            {/* Read-only details */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <DetailRow label="Email" value={prospect.email} testId="detail-email" />
              <DetailRow label="Pays" value={prospect.country} testId="detail-country" />
              <DetailRow label="Langue email" value={LANG_LABEL[detectedLang] || detectedLang} testId="detail-language" />
              <DetailRow label="Contact" value={prospect.contact_name} testId="detail-contact-name" />
              <DetailRow label="Téléphone" value={prospect.phone} testId="detail-phone" />
              <DetailRow label="Site web" value={prospect.website} testId="detail-website" link />
            </div>

            <div className="border-t border-zinc-800 pt-3">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Spécialité / Domaine</Label>
              <p className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap" data-testid="detail-specialty">
                {prospect.specialty || <span className="text-zinc-600 italic">Non renseignée — l'agent IA s'en servira pour personnaliser ses emails. Cliquez sur Modifier pour l'ajouter.</span>}
              </p>
            </div>

            {prospect.notes && (
              <div className="border-t border-zinc-800 pt-3">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Notes internes</Label>
                <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap" data-testid="detail-notes">{prospect.notes}</p>
              </div>
            )}

            {/* History */}
            <div className="border-t border-zinc-800 pt-3">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> Historique
              </Label>
              {logsLoading ? (
                <p className="text-xs text-zinc-600 mt-2">Chargement…</p>
              ) : logs.length === 0 ? (
                <p className="text-xs text-zinc-600 mt-2 italic">Aucun événement enregistré.</p>
              ) : (
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="detail-history">
                  {logs.slice(0, 30).map((log, i) => {
                    const meta = LOG_LABEL[log.type] || { label: log.type, color: 'text-zinc-300', icon: AlertTriangle };
                    const Icon = meta.icon;
                    const isReply = REPLY_TYPES.has(log.type);
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-2 text-xs border-l-2 border-zinc-800 pl-2.5 py-0.5 ${isReply ? 'cursor-pointer hover:bg-zinc-800/50 hover:border-amber-500 rounded-r-sm transition-colors' : ''}`}
                        onClick={isReply ? () => setOpenReplyLog(log) : undefined}
                        data-testid={isReply ? `reply-log-${i}` : undefined}
                      >
                        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-medium ${meta.color}`}>{meta.label}</span>
                            <span className="text-zinc-600">{new Date(log.created_at).toLocaleString('fr-FR')}</span>
                            {isReply && log.from_email && (
                              <span className="text-[10px] font-mono text-zinc-500 truncate">{log.from_email}</span>
                            )}
                          </div>
                          {log.subject && <p className="text-zinc-300 truncate">{log.subject}</p>}
                          {log.type === 'status_change' && (log.status_from || log.status_to) && (
                            <p className="text-zinc-500">
                              {log.status_from || '—'} → <strong className="text-zinc-300">{log.status_to || '—'}</strong>
                            </p>
                          )}
                          {isReply && log.body && (
                            <p className="text-zinc-500 mt-0.5 whitespace-pre-wrap line-clamp-2">{log.body.slice(0, 180)}…</p>
                          )}
                          {isReply && (
                            <p className="text-amber-400 text-[10px] mt-0.5 flex items-center gap-1">
                              <Eye className="w-3 h-3" /> Voir le mail complet
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter className="!justify-between border-t border-zinc-800 pt-3 mt-2">
              <Button variant="outline" onClick={handleDelete} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-sm" data-testid="detail-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
              </Button>
              <Button onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm">Fermer</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-400">Nom de l'entreprise *</Label>
              <Input data-testid="edit-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input data-testid="edit-email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Contact (prénom / nom)</Label>
                <Input data-testid="edit-contact-name" value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Pays</Label>
                <Select value={form.country} onValueChange={(v) => setForm(f => ({ ...f, country: v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="edit-country"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-60">
                    {POPULAR_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Langue email</Label>
                <Select value={form.language || 'auto'} onValueChange={(v) => setForm(f => ({ ...f, language: v === 'auto' ? '' : v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="edit-language"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    <SelectItem value="auto">Auto ({LANG_LABEL[detectedLang]})</SelectItem>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                    <SelectItem value="es">🇪🇸 Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">
                Spécialité / Domaine
                <span className="text-amber-400/80 ml-1.5">— utilisée par l'agent IA pour personnaliser le ton de l'email</span>
              </Label>
              <Textarea data-testid="edit-specialty" rows={2} value={form.specialty} onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" placeholder="Ex: Tôlerie fine acier inox pour aéronautique, série courte, tolérances ±0.05mm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Téléphone</Label>
                <Input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Site web</Label>
                <Input data-testid="edit-website" value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" placeholder="https://…" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes internes</Label>
              <Textarea data-testid="edit-notes" rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdit(false)} className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="cancel-edit-btn">
                <X className="w-3.5 h-3.5 mr-1.5" /> Annuler
              </Button>
              <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="save-edit-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
      <ReplyDetailDialog log={openReplyLog} open={!!openReplyLog} onOpenChange={(v) => !v && setOpenReplyLog(null)} />
    </Dialog>
  );
}

// Dialog showing the FULL prospect email — full body + AI draft + send action
function ReplyDetailDialog({ log, open, onOpenChange }) {
  const [sending, setSending] = useState(false);
  if (!log) return null;
  const intent = log.type === 'gmail_positive_reply' ? 'positive'
    : log.type === 'gmail_negative_reply' ? 'negative' : 'neutral';
  const headerColor = intent === 'positive' ? 'text-emerald-300'
    : intent === 'negative' ? 'text-rose-300' : 'text-sky-300';
  const headerLabel = intent === 'positive' ? '🟢 Prospect intéressé'
    : intent === 'negative' ? '❌ Refus reçu' : '📩 Réponse reçue';
  const bodyText = log.body_full || log.body || '(aucun contenu)';
  const hasDraft = !!(log.draft_body && log.draft_body.trim());

  const sendDraft = async () => {
    if (!log.gmail_log_id) {
      toast.error('Impossible — log Gmail introuvable');
      return;
    }
    if (!window.confirm('Envoyer la réponse pré-rédigée par l\'IA ?')) return;
    setSending(true);
    try {
      await api.post('/gmail/send-reply', {
        gmail_log_id: log.gmail_log_id,
        subject: log.draft_subject || `Re: ${log.subject || ''}`,
        body: log.draft_body,
      });
      toast.success('Réponse envoyée ✓');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="reply-detail-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${headerColor}`}>{headerLabel}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {log.from_name ? `${log.from_name} · ` : ''}
            <span className="font-mono text-xs">{log.from_email || '—'}</span>
            {' · '}
            <span className="text-zinc-500">{new Date(log.created_at).toLocaleString('fr-FR')}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Sujet</Label>
            <div className="text-sm text-zinc-200 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 mt-1">
              {log.subject || '(sans objet)'}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Message complet du prospect</Label>
            <div className="text-sm text-zinc-200 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 max-h-72 overflow-y-auto whitespace-pre-wrap mt-1" data-testid="reply-full-body">
              {bodyText}
            </div>
          </div>
          {hasDraft && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Brouillon IA pré-rédigé
              </Label>
              <div className="text-xs text-zinc-400 mb-1 italic mt-1">Objet : {log.draft_subject || `Re: ${log.subject || ''}`}</div>
              <div className="text-sm text-zinc-200 bg-amber-950/30 border border-amber-900/40 rounded-sm px-3 py-2 max-h-48 overflow-y-auto whitespace-pre-wrap" data-testid="reply-draft-body">
                {log.draft_body}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">
            Fermer
          </Button>
          {log.gmail_log_id && (
            <Button asChild variant="outline" className="border-zinc-700 text-zinc-300 rounded-sm" data-testid="open-in-mail-btn">
              <a href={`/mail?focus=${log.gmail_log_id}`}>
                <Mail className="w-3.5 h-3.5 mr-1" /> Ouvrir dans Mail
              </a>
            </Button>
          )}
          {hasDraft && intent === 'positive' && (
            <Button onClick={sendDraft} disabled={sending} className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm" data-testid="send-ai-draft-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Envoyer la réponse IA
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, testId, link }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <p className="text-zinc-200 mt-0.5 break-words" data-testid={testId}>
        {value
          ? (link
              ? <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:underline inline-flex items-center gap-1">{value}<ExternalLink className="w-3 h-3" /></a>
              : value)
          : <span className="text-zinc-600">—</span>}
      </p>
    </div>
  );
}


function DashboardTab({ stats, perfDays, setPerfDays, companies, emails, mapData }) {
  const emailsByDay = (() => {
    const map = {};
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(5, 10);
      map[key] = 0;
    }
    emails.forEach(e => {
      const key = (e.created_at || '').slice(5, 10);
      if (key in map) map[key]++;
    });
    return Object.entries(map).map(([day, count]) => ({ day, count }));
  })();

  const byCountry = (() => {
    const m = {};
    companies.forEach(c => { const k = c.country || 'Inconnu'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([country, count]) => ({ country, count })).sort((a, b) => b.count - a.count).slice(0, 8);
  })();

  const byStatus = (() => {
    const m = {};
    companies.forEach(c => { const k = c.status || 'nouveau'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name: STATUS_LABEL[name]?.label || name, value }));
  })();
  const PIE_COLORS = ['#fbbf24', '#34d399', '#a78bfa', '#60a5fa', '#f87171', '#9ca3af', '#fb923c'];

  return (
    <div className="space-y-4" data-testid="dashboard-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Performance des {perfDays} derniers jours</p>
        <Select value={String(perfDays)} onValueChange={(v) => setPerfDays(parseInt(v))}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-200 rounded-sm w-32 h-8 text-xs" data-testid="dashboard-period"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
            <SelectItem value="7">7 jours</SelectItem>
            <SelectItem value="30">30 jours</SelectItem>
            <SelectItem value="90">90 jours</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Emails envoyés" value={stats?.summary?.emails_sent_in_period || 0} icon={Send} color="text-emerald-400" testId="kpi-sent" />
        <StatCard label="Réponses positives" value={stats?.summary?.positive_replies_in_period || 0} icon={CheckCircle2} color="text-violet-400" testId="kpi-pos-replies" />
        <StatCard label="Taux de réponse" value={`${stats?.summary?.reply_rate || 0}%`} icon={Eye} color="text-sky-400" testId="kpi-reply-rate" />
        <StatCard label="Clients gagnés" value={stats?.summary?.clients_won || 0} icon={Sparkles} color="text-amber-400" testId="kpi-clients" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Emails envoyés / jour (14j)</p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailsByDay}>
                  <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#71717a', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '2px' }} labelStyle={{ color: '#a1a1aa' }} itemStyle={{ color: '#f4f4f5' }} />
                  <Bar dataKey="count" fill="hsl(38, 92%, 50%)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Répartition par statut</p>
            <div className="h-44 flex items-center gap-3">
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {byStatus.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1">
                {byStatus.map((s, i) => (
                  <div key={s.name} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-zinc-400 flex-1">{s.name}</span>
                    <span className="text-zinc-200 font-mono">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Carte des prospects actifs</p>
            <p className="text-[10px] text-zinc-600">{(mapData || []).reduce((s, c) => s + (c.count || 0), 0)} prospect(s) sur {(mapData || []).length} pays · désinscrits exclus</p>
          </div>
          <WorldMap mapData={mapData || []} />
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Top 8 pays</p>
          <div className="space-y-1.5">
            {byCountry.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-3">Aucun prospect</p>
            ) : byCountry.map((c, i) => {
              const max = byCountry[0]?.count || 1;
              const pct = (c.count / max) * 100;
              return (
                <div key={c.country} className="flex items-center gap-3 text-xs" data-testid={`country-${i}`}>
                  <span className="text-zinc-300 w-32 truncate">{c.country}</span>
                  <div className="flex-1 bg-zinc-800 rounded-sm h-4 overflow-hidden">
                    <div className="bg-amber-500 h-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-zinc-400 font-mono w-8 text-right">{c.count}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsTab({ state, onSaved }) {
  const [form, setForm] = useState({
    mode: 'off', daily_email_limit: 40, max_emails_per_run: 5, run_interval_minutes: 30,
    active_days: [1,2,3,4,5], start_hour: 9, end_hour: 18, default_tone: 'pro',
    relance_1_days: 4, relance_2_days: 8, relance_3_days: 14, same_domain_cooldown_hours: 72,
    company_pitch: '', custom_ai_instructions: '', signature: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/ai-agent/settings');
        setForm(f => ({ ...f, ...data }));
        setLoaded(true);
      } catch (e) { toast.error('Erreur chargement paramètres'); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/ai-agent/settings', form);
      toast.success('Paramètres enregistrés');
      onSaved?.();
    } catch (e) {
      // Defensive: FastAPI/Pydantic 422 returns `detail` as an array of objects.
      // Coerce anything non-string to a readable string so toast (=> React render) never crashes.
      const raw = e?.response?.data?.detail;
      let msg = 'Erreur';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      else if (raw && typeof raw === 'object') msg = raw.msg || JSON.stringify(raw);
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-center py-12 text-zinc-500">Chargement…</div>;

  const DAYS = ['L','M','M','J','V','S','D'];
  const toggleDay = (idx) => {
    const dayNum = idx + 1;
    setForm(f => ({ ...f, active_days: f.active_days.includes(dayNum) ? f.active_days.filter(d => d !== dayNum) : [...f.active_days, dayNum].sort() }));
  };

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wider">Mode de fonctionnement</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Pause = aucun envoi. Auto = l'agent envoie selon la cadence ci-dessous.</p>
            </div>
            <div className="flex items-center gap-2 border border-zinc-800 rounded-sm p-1 bg-zinc-950">
              <button data-testid="mode-pause" onClick={() => setForm(f => ({ ...f, mode: 'off' }))} className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 ${(form.mode === 'off' || form.mode === 'semi_auto' || form.mode === 'manual') ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}>
                <Pause className="w-3.5 h-3.5" /> Pause
              </button>
              <button data-testid="mode-auto" onClick={() => setForm(f => ({ ...f, mode: 'auto' }))} className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 ${form.mode === 'auto' ? 'bg-emerald-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}>
                <Play className="w-3.5 h-3.5" /> Auto
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wider">Cadence d'envoi</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Max emails / jour</Label>
              <Input data-testid="daily-limit" type="number" min="0" max="500" value={form.daily_email_limit} onChange={(e) => setForm(f => ({ ...f, daily_email_limit: parseInt(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Max par cycle</Label>
              <Input data-testid="per-run-limit" type="number" min="1" max="50" value={form.max_emails_per_run} onChange={(e) => setForm(f => ({ ...f, max_emails_per_run: parseInt(e.target.value) || 1 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Intervalle (min)</Label>
              <Input data-testid="run-interval" type="number" min="5" max="1440" value={form.run_interval_minutes} onChange={(e) => setForm(f => ({ ...f, run_interval_minutes: parseInt(e.target.value) || 30 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-zinc-400">Jours actifs</Label>
              <div className="flex gap-1 mt-1">
                {DAYS.map((d, i) => {
                  const active = form.active_days.includes(i + 1);
                  return <button key={i} onClick={() => toggleDay(i)} data-testid={`day-${i}`} className={`w-9 h-9 rounded-sm text-xs font-medium ${active ? 'bg-amber-500 text-zinc-950' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>{d}</button>;
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Plage horaire</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="start-hour" type="number" min="0" max="23" value={form.start_hour} onChange={(e) => setForm(f => ({ ...f, start_hour: parseInt(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm w-20" />
                <span className="text-zinc-500 text-xs">→</span>
                <Input data-testid="end-hour" type="number" min="0" max="23" value={form.end_hour} onChange={(e) => setForm(f => ({ ...f, end_hour: parseInt(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm w-20" />
                <span className="text-zinc-500 text-xs">h</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wider">Relances & ton</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">1ère relance (j)</Label>
              <Input data-testid="relance-1" type="number" min="1" max="60" value={form.relance_1_days} onChange={(e) => setForm(f => ({ ...f, relance_1_days: parseInt(e.target.value) || 4 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">2ème relance (j)</Label>
              <Input data-testid="relance-2" type="number" min="1" max="60" value={form.relance_2_days} onChange={(e) => setForm(f => ({ ...f, relance_2_days: parseInt(e.target.value) || 8 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">3ème relance (j)</Label>
              <Input data-testid="relance-3" type="number" min="1" max="60" value={form.relance_3_days} onChange={(e) => setForm(f => ({ ...f, relance_3_days: parseInt(e.target.value) || 14 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Cooldown même domaine (h)</Label>
              <Input data-testid="domain-cooldown" type="number" min="0" max="720" value={form.same_domain_cooldown_hours} onChange={(e) => setForm(f => ({ ...f, same_domain_cooldown_hours: parseInt(e.target.value) || 72 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Ton par défaut</Label>
              <Select value={form.default_tone} onValueChange={(v) => setForm(f => ({ ...f, default_tone: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="default-tone"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                  <SelectItem value="pro">Professionnel</SelectItem>
                  <SelectItem value="commercial">Commercial dynamique</SelectItem>
                  <SelectItem value="amical">Amical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wider">Prompts & instructions IA</h3>
          <p className="text-[11px] text-zinc-500">Ces textes sont injectés dans CHAQUE email généré, dans toutes les langues.</p>
          <div>
            <Label className="text-xs text-zinc-400">Pitch entreprise *</Label>
            <Textarea data-testid="company-pitch" rows={4} value={form.company_pitch} onChange={(e) => setForm(f => ({ ...f, company_pitch: e.target.value }))} placeholder="Présente ProdLink en 3-5 lignes : ce que tu fais, tes différenciateurs…" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Instructions custom IA <span className="text-zinc-600">(à respecter dans chaque email)</span></Label>
            <Textarea data-testid="custom-instructions" rows={4} value={form.custom_ai_instructions} onChange={(e) => setForm(f => ({ ...f, custom_ai_instructions: e.target.value }))} placeholder='Ex: "Ne mentionne jamais de prix dans le 1er email" · "Termine par une question ouverte" · "Reste sous 100 mots".' className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Signature</Label>
            <Textarea data-testid="signature" rows={3} value={form.signature} onChange={(e) => setForm(f => ({ ...f, signature: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 font-mono text-xs" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <Button
          data-testid="send-test-email-btn"
          variant="outline"
          onClick={async () => {
            if (!window.confirm('Envoyer un email de TEST à prodlinkfr@gmail.com ?\nLe LLM générera un email réel basé sur les paramètres actuels.')) return;
            try {
              const { data } = await api.post('/ai-agent/agent/test-email');
              toast.success(`Email test envoyé à ${data.to} (Resend ID: ${(data.resend_id || '').slice(0, 12)}…)`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Erreur envoi test');
            }
          }}
          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm"
        >
          <Mail className="w-4 h-4 mr-2" /> Envoyer un email de test
        </Button>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </div>
  );
}

function WorldMap({ mapData }) {
  const withCoords = (mapData || []).filter(c => typeof c.lat === 'number' && typeof c.lng === 'number');
  const sansCoords = (mapData || []).filter(c => !c.lat);
  const maxCount = Math.max(1, ...withCoords.map(c => c.count || 0));
  const [hover, setHover] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const totalProspects = withCoords.reduce((s, c) => s + (c.count || 0), 0);
  const totalInterested = withCoords.reduce(
    (s, c) => s + ((c.by_status?.interesse || 0) + (c.by_status?.repondu || 0)),
    0,
  );

  return (
    <div className="relative w-full" data-testid="world-map">
      <div
        className="relative rounded-sm overflow-hidden border border-zinc-800/60"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 45%, #0b1822 0%, #060a0f 70%, #050709 100%)',
        }}
      >
        {/* Decorative grid + glow */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.08]"
             style={{
               backgroundImage:
                 'linear-gradient(rgba(251,191,36,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.4) 1px, transparent 1px)',
               backgroundSize: '40px 40px',
             }}
        />
        <RsmMap
          geoUrl={WORLD_GEO_URL}
          markers={withCoords}
          maxCount={maxCount}
          hover={hover}
          onHover={(country) => setHover(country)}
          onTooltip={setTooltip}
        />
        {/* Floating tooltip */}
        {tooltip && (
          <div
            className="absolute pointer-events-none bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 shadow-2xl z-10"
            style={{ left: tooltip.x + 12, top: tooltip.y + 12, maxWidth: 220 }}
            data-testid="map-tooltip"
          >
            <div className="font-semibold text-amber-300">{tooltip.country}</div>
            <div className="text-zinc-300">
              <strong>{tooltip.count}</strong> prospect{tooltip.count > 1 ? 's' : ''}
            </div>
            {tooltip.by_status?.interesse > 0 && (
              <div className="text-emerald-300 text-[10px] mt-0.5">
                ✓ {tooltip.by_status.interesse} intéressé{tooltip.by_status.interesse > 1 ? 's' : ''}
              </div>
            )}
            {tooltip.by_status?.repondu > 0 && (
              <div className="text-violet-300 text-[10px]">
                ↩ {tooltip.by_status.repondu} a répondu
              </div>
            )}
            {tooltip.by_status?.contacte > 0 && (
              <div className="text-amber-300 text-[10px]">
                ✉ {tooltip.by_status.contacte} contacté{tooltip.by_status.contacte > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Compact stat overlay top-left */}
        <div className="absolute top-3 left-3 flex flex-col gap-1 bg-zinc-950/70 backdrop-blur-sm border border-zinc-800 rounded-sm px-2.5 py-1.5 text-[10px] text-zinc-400 pointer-events-none">
          <div>
            <span className="text-zinc-500">Pays couverts : </span>
            <strong className="text-zinc-100">{withCoords.length}</strong>
          </div>
          <div>
            <span className="text-zinc-500">Total prospects : </span>
            <strong className="text-amber-300">{totalProspects}</strong>
          </div>
          {totalInterested > 0 && (
            <div>
              <span className="text-zinc-500">Intéressés / répondus : </span>
              <strong className="text-emerald-300">{totalInterested}</strong>
            </div>
          )}
        </div>
      </div>

      {sansCoords.length > 0 && (
        <p className="text-[10px] text-zinc-600 mt-2">
          {sansCoords.length} pays sans coordonnées : {sansCoords.map(c => `${c.country} (${c.count})`).join(', ')}
        </p>
      )}
      <div className="flex items-center gap-4 text-[10px] text-zinc-500 mt-2 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#fbbf24', boxShadow: '0 0 6px #fbbf24' }} />
          Prospect actif
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#34d399', boxShadow: '0 0 6px #34d399' }} />
          Au moins 1 intéressé / répondu
        </span>
        <span className="text-zinc-700">·</span>
        <span>Taille du point ∝ nombre de prospects</span>
      </div>
    </div>
  );
}

// Choropleth + label map. Countries are coloured by intensity, with a discreet
// pill-label (count) anchored near the centroid. No more big pulsing dots.
function RsmMap({ geoUrl, markers, maxCount, hover, onHover, onTooltip }) {
  // Build a lookup: TopoJSON country name → marker (with count + status colour).
  const byIsoName = {};
  markers.forEach((m) => {
    if (m.iso_name) byIsoName[m.iso_name] = m;
  });

  const intensity = (count) => {
    if (!count || maxCount <= 0) return 0;
    // gamma-curve so small counts are still visible but big ones don't saturate
    return Math.min(1, Math.pow(count / maxCount, 0.55));
  };

  // Convert a 0-1 intensity → choropleth fill colour.
  // Two ramps depending on whether the country has "interesse/repondu" prospects.
  // We use SOLID colours (no alpha) so the fill cleanly overrides the default
  // landGradient — alpha-blending was getting muddied by the dark base layer.
  const colourFor = (m) => {
    if (!m) return null;
    const i = intensity(m.count);
    const hasInterested = ((m.by_status?.interesse || 0) + (m.by_status?.repondu || 0)) > 0;
    if (hasInterested) {
      // emerald 700 → 300
      const r = Math.round(4   + (110 - 4)   * i);
      const g = Math.round(120 + (231 - 120) * i);
      const b = Math.round(87  + (183 - 87)  * i);
      return `rgb(${r}, ${g}, ${b})`;
    }
    // amber 800 → 300
    const r = Math.round(146 + (252 - 146) * i);
    const g = Math.round(64  + (211 - 64)  * i);
    const b = Math.round(14  + (77  - 14)  * i);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <ComposableMap
      data-testid="rsm-map"
      projectionConfig={{ scale: 145, center: [10, 25] }}
      width={820}
      height={400}
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      <defs>
        <radialGradient id="oceanShine" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#0e1e2b" />
          <stop offset="100%" stopColor="#070a0f" />
        </radialGradient>
        <linearGradient id="landGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1b2533" />
          <stop offset="100%" stopColor="#111722" />
        </linearGradient>
        <filter id="labelShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.4" floodColor="#000" floodOpacity="0.6" />
        </filter>
      </defs>
      <rect x="0" y="0" width="820" height="400" fill="url(#oceanShine)" />
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map((geo) => {
            const name = geo.properties?.name;
            const m = byIsoName[name];
            const fill = m ? colourFor(m) : 'url(#landGradient)';
            const isActive = !!m;
            const isHover = hover && hover === name;
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                data-testid={isActive ? `country-active-${name}` : undefined}
                fill={isActive ? fill : '#1a2230'}
                stroke={isActive ? 'rgba(251,191,36,0.85)' : '#2d3a4d'}
                strokeWidth={isActive ? 0.8 : 0.4}
                onMouseEnter={(e) => {
                  onHover?.(name);
                  if (m) {
                    const svg = e.target.ownerSVGElement;
                    const rect = svg.getBoundingClientRect();
                    onTooltip?.({ ...m, x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }
                }}
                onMouseMove={(e) => {
                  if (m) {
                    const svg = e.target.ownerSVGElement;
                    const rect = svg.getBoundingClientRect();
                    onTooltip?.({ ...m, x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }
                }}
                onMouseLeave={() => { onHover?.(null); onTooltip?.(null); }}
                style={{
                  default: { outline: 'none', transition: 'fill 200ms, stroke 200ms', cursor: isActive ? 'pointer' : 'default' },
                  hover:   { outline: 'none', stroke: isActive ? '#fbbf24' : '#3d5274', strokeWidth: isActive ? 1.4 : 0.6, filter: isActive ? 'brightness(1.15)' : undefined },
                  pressed: { outline: 'none' },
                }}
              />
            );
          })
        }
      </Geographies>

      {/* Discreet count labels anchored at each country centroid */}
      {/* Small per-country offsets (lng, lat) to avoid stacking in Europe where
          France / Belgium / Switzerland / Italy are geographically very close. */}
      {(() => {
        const LABEL_OFFSETS = {
          'France':            [-6, 1],
          'Belgium':           [-2, 5],
          'Switzerland':       [4, 5],
          'Italy':             [6, -3],
          'Germany':           [2, 6],
          'Netherlands':       [-1, 7],
          'Luxembourg':        [-4, 3],
          'United Kingdom':    [-10, 4],
          'Spain':             [-8, -3],
          'Portugal':          [-12, -5],
        };
        return markers.map((m, i) => {
          if (typeof m.lat !== 'number' || typeof m.lng !== 'number') return null;
          const hasInterested = ((m.by_status?.interesse || 0) + (m.by_status?.repondu || 0)) > 0;
          const dotColor = hasInterested ? '#34d399' : '#fbbf24';
          const labelText = String(m.count);
          const padX = 5;
          const charW = 6.2;
          const w = labelText.length * charW + padX * 2 + 8;
          const [dx, dy] = LABEL_OFFSETS[m.iso_name] || [0, 0];
          // The leader line goes from the country centroid (0,0 in marker space)
          // to the label anchor offset (dx, dy) — projected to SVG pixels via the marker.
          // We approximate the pixel offset using fixed multipliers tuned at the current scale.
          const px = dx * 3.5;
          const py = -dy * 3.5; // negate because SVG y grows downward
          return (
            <Marker key={i} coordinates={[m.lng, m.lat]} data-testid={`map-marker-${m.country}`}>
              <g style={{ pointerEvents: 'none' }}>
                {(px !== 0 || py !== 0) && (
                  <line
                    x1={0} y1={0} x2={px} y2={py}
                    stroke={dotColor}
                    strokeWidth={0.9}
                    strokeOpacity={0.55}
                  />
                )}
                <circle cx={0} cy={0} r={1.8} fill={dotColor} opacity={0.9} />
                <g transform={`translate(${px} ${py})`} filter="url(#labelShadow)">
                  <rect
                    x={-w / 2}
                    y={-9}
                    width={w}
                    height={18}
                    rx={9}
                    fill="rgba(9,12,18,0.95)"
                    stroke={dotColor}
                    strokeWidth={1.1}
                  />
                  <circle cx={-w / 2 + 8} cy={0} r={2.6} fill={dotColor}>
                    <animate attributeName="opacity" values="0.5;1;0.5" dur="1.8s" repeatCount="indefinite" />
                  </circle>
                  <text
                    x={-w / 2 + 14}
                    y={3}
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    fill="#fafafa"
                  >
                    {labelText}
                  </text>
                </g>
              </g>
            </Marker>
          );
        });
      })()}
    </ComposableMap>
  );
}



/* ============================================================
   AutoModeActivityPanel
   Visible only when settings.mode === 'auto'.
   Shows a live, animated indicator of the agent's current activity.
   ============================================================ */
function AutoModeActivityPanel({ state, onRunNow, runningNow }) {
  const running = !!state?.running;
  const inWindow = !!state?.active_window;
  const processed = Number(state?.current_processed || 0);
  const targetCount = Number(state?.current_target_count || 0);
  const sentToday = Number(state?.sent_today || 0);
  const dailyLimit = Number(state?.daily_email_limit || 0);
  const intervalMin = Number(state?.run_interval_minutes || 30);
  const nextRunAt = state?.next_run_at;
  // Build a human-readable "next cycle in X" hint when we have a scheduled next_run_at.
  let nextRunHint = null;
  if (nextRunAt) {
    const diffMs = new Date(nextRunAt).getTime() - Date.now();
    if (diffMs > 0) {
      const mins = Math.round(diffMs / 60000);
      if (mins <= 1) nextRunHint = 'dans < 1 min';
      else if (mins < 60) nextRunHint = `dans ${mins} min`;
      else nextRunHint = `dans ${Math.round(mins / 60)} h`;
    } else {
      nextRunHint = 'imminent';
    }
  }
  const lastSummary = state?.last_summary || {};
  const skippedReason = state?.last_scheduler_skipped_reason;

  // Status flavour determines colour + messages
  // - running  => green, full activity
  // - inWindow => amber, "veille active"
  // - else     => zinc, "hors plage"
  const flavour = running ? 'green' : (inWindow ? 'amber' : 'zinc');

  const palette = {
    green: {
      ring: 'border-emerald-500/40',
      bg: 'from-emerald-500/10 via-emerald-500/5 to-transparent',
      text: 'text-emerald-300',
      icon: 'text-emerald-300 bg-emerald-500/15',
      radar: '',
      label: 'En cours',
      labelBg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    },
    amber: {
      ring: 'border-amber-500/30',
      bg: 'from-amber-500/10 via-amber-500/5 to-transparent',
      text: 'text-amber-200',
      icon: 'text-amber-300 bg-amber-500/15',
      radar: 'amber',
      label: 'Actif · Prêt',
      labelBg: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    },
    zinc: {
      ring: 'border-zinc-700/60',
      bg: 'from-zinc-700/10 via-zinc-700/5 to-transparent',
      text: 'text-zinc-300',
      icon: 'text-zinc-300 bg-zinc-700/40',
      radar: 'zinc',
      label: 'Standby',
      labelBg: 'bg-zinc-700/40 text-zinc-300 border-zinc-700',
    },
  }[flavour];

  const messages = running
    ? [
        'Recherche des prospects à contacter…',
        'Détection de la langue selon le pays…',
        'Rédaction des emails personnalisés avec Claude…',
        'Envoi en cours via Resend…',
      ]
    : inWindow
    ? [
        nextRunHint ? `Prochain envoi automatique ${nextRunHint}` : `Prochain envoi automatique dans ~${intervalMin} min`,
        'Surveillance des réponses Gmail en arrière-plan…',
        'Agent actif — prêt à enchaîner les envois',
        'Analyse des intentions « intéressé » des réponses…',
      ]
    : [
        'Hors plage horaire — l\'agent est en pause',
        'Reprise automatique pendant les heures de travail',
        'Aucun envoi pendant les week-ends/nuit',
        'Vous pouvez forcer un envoi manuel quand vous voulez',
      ];

  const progressPct = targetCount > 0 ? Math.min(100, Math.round((processed / targetCount) * 100)) : 0;
  const limitPct = dailyLimit > 0 ? Math.min(100, Math.round((sentToday / dailyLimit) * 100)) : 0;

  return (
    <div
      data-testid="auto-mode-activity-panel"
      data-status={flavour}
      data-running={running ? '1' : '0'}
      className={`relative rounded-sm border ${palette.ring} bg-gradient-to-r ${palette.bg} px-4 py-3 ${running ? 'agent-activity-panel' : (inWindow ? 'agent-idle-panel' : '')}`}
    >
      <div className="relative z-10 flex items-center gap-4">
        {/* Animated bot icon — bobs in both running AND idle-in-window modes
            so the panel always feels alive. Only stops when fully out-of-window. */}
        <div className={`relative w-10 h-10 rounded-sm ${palette.icon} flex items-center justify-center shrink-0`}>
          <Bot className={`w-5 h-5 ${(running || inWindow) ? 'agent-bot-bob' : ''}`} aria-hidden="true" />
          {(running || inWindow) && (
            <Sparkles className={`w-3 h-3 absolute -top-1 -right-1 ${running ? 'text-emerald-300' : 'text-amber-300'} agent-sparkle`} aria-hidden="true" />
          )}
        </div>

        {/* Status + scrolling message */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="agent-radar" aria-hidden="true">
              <span className={`ring ${palette.radar}`}></span>
              <span className={`ring delay ${palette.radar}`}></span>
              <span className="dot"></span>
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm border ${palette.labelBg}`} data-testid="auto-mode-status-label">
              {palette.label}
            </span>
            <span className="text-[10px] text-zinc-500">
              Mode Auto · {intervalMin} min · {state?.active_days?.length || 0} jours actifs
            </span>
          </div>
          {/* Scrolling messages */}
          <div className={`agent-marquee mt-1 text-xs ${palette.text}`} aria-live="polite">
            <div className="agent-marquee-track">
              {messages.map((m, i) => (
                <span key={i} data-testid={`auto-msg-${i}`}>{m}</span>
              ))}
              {/* duplicate first so the loop reads naturally */}
              <span>{messages[0]}</span>
            </div>
          </div>
          {/* Live background-task pills — always pulsing when idle-in-window
              to demonstrate that the agent IS still working between cycles
              (Gmail polling, intent analysis, prospect scoring), not asleep. */}
          {!running && inWindow && (
            <div className="hidden sm:flex items-center gap-1.5 mt-1.5" data-testid="agent-bg-pills">
              <span className="agent-bg-pill" title="Surveille les réponses Gmail en arrière-plan">
                <span className="pulse" />
                <Mail className="w-2.5 h-2.5" /> Gmail
              </span>
              <span className="agent-bg-pill delay-1" title="Analyse les intentions « intéressé » des réponses reçues">
                <span className="pulse" />
                <Sparkles className="w-2.5 h-2.5" /> Intentions
              </span>
              <span className="agent-bg-pill delay-2" title="Calcule les scores IA des prospects">
                <span className="pulse" />
                <BarChart3 className="w-2.5 h-2.5" /> Scoring
              </span>
            </div>
          )}
        </div>

        {/* Right stats */}
        <div className="hidden md:flex flex-col items-end gap-1 shrink-0 text-[10px] text-zinc-400">
          <div className="flex items-center gap-3">
            <span><strong className="text-zinc-100">{sentToday}</strong>/{dailyLimit} aujourd&apos;hui</span>
            <span className="text-zinc-700">·</span>
            <span><strong className="text-zinc-100">{lastSummary.sent || 0}</strong> envoyés au dernier cycle</span>
          </div>
          {running && targetCount > 0 && (
            <div className="text-emerald-300" data-testid="auto-progress-label">
              Traitement {processed}/{targetCount} ({progressPct}%)
            </div>
          )}
          {!running && skippedReason && (
            <div className="text-zinc-500 italic max-w-xs truncate" title={skippedReason}>
              Dernier skip : {skippedReason}
            </div>
          )}
          {/* Top skip reasons from the previous cycle — collapsed by default
              into a small label with a hover tooltip that reveals the breakdown.
              Keeps the panel clean while still surfacing the diagnostic on demand. */}
          {!running && Array.isArray(lastSummary?.top_skip_reasons) && lastSummary.top_skip_reasons.length > 0 && (() => {
            const tooltipText = lastSummary.top_skip_reasons
              .slice(0, 5)
              .map(r => `${r.count}× ${r.reason}`)
              .join('\n');
            return (
              <div
                className="text-[10px] text-zinc-500 italic underline decoration-dotted decoration-zinc-700 underline-offset-2 cursor-help"
                data-testid="top-skip-reasons-label"
                title={tooltipText}
              >
                Top raisons skip
              </div>
            );
          })()}
        </div>

        {/* "Lancer maintenant" — force an immediate cycle so the admin doesn't
            have to wait the 10-min interval. Only shown when the agent is idle
            AND within its active window. */}
        {!running && inWindow && onRunNow && (
          <button
            type="button"
            onClick={onRunNow}
            disabled={!!runningNow}
            data-testid="run-agent-now-btn"
            className="shrink-0 ml-2 px-3 py-1.5 rounded-sm bg-amber-500 text-zinc-950 text-xs font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            title="Forcer un cycle d'envoi immédiat"
          >
            {runningNow ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lancement…</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> Lancer maintenant</>
            )}
          </button>
        )}
      </div>

      {/* Progress bar — only when actively running and we have a target */}
      {running && targetCount > 0 && (
        <div className="relative z-10 mt-2 agent-progress-track" data-testid="auto-progress-bar">
          <div className="agent-progress-fill" style={{ right: `${100 - progressPct}%` }} />
        </div>
      )}

      {/* Daily-limit micro bar when idle */}
      {!running && dailyLimit > 0 && (
        <div className="relative z-10 mt-2 h-1 rounded-full bg-white/5 overflow-hidden" aria-label="Quota envoi quotidien">
          <div
            className={`${flavour === 'amber' ? 'bg-amber-400/70' : 'bg-zinc-500/70'} h-full transition-all`}
            style={{ width: `${limitPct}%` }}
          />
        </div>
      )}

      {/* Quota diagnostic — explains WHY today's send count might be below daily_email_limit */}
      {state?.diagnostic && (() => {
        const d = state.diagnostic;
        const blockers = [];
        if (d.without_email > 0) blockers.push({ icon: '📭', label: `${d.without_email} prospect(s) sans email`, hint: "Ajoutez leurs emails pour qu'ils soient contactés." });
        if (d.ready_to_send === 0 && d.fresh_with_email === 0) blockers.push({ icon: '⏳', label: 'Aucune relance due', hint: 'Les prospects déjà contactés attendent leur prochaine fenêtre de relance.' });
        // Quota possible = min(daily_limit - sent_today, ready_to_send)
        const remainingQuota = Math.max(0, dailyLimit - sentToday);
        const maxPossibleToday = Math.min(remainingQuota, d.ready_to_send || 0);
        return (
          <div className="relative z-10 mt-3 pt-3 border-t border-zinc-800/60" data-testid="quota-diagnostic">
            <div className="flex items-center justify-between text-[11px] flex-wrap gap-2">
              <span className="text-zinc-400">
                <strong className="text-zinc-100">{maxPossibleToday}</strong> envoi(s) max possible(s) aujourd'hui
                {maxPossibleToday < remainingQuota && (
                  <span className="text-zinc-600"> (quota restant : {remainingQuota})</span>
                )}
              </span>
              <span className="text-zinc-500 text-[10px]">
                {d.fresh_with_email} nouveau(x) · {d.followups_due} relance(s) due(s) · {d.without_email} sans email
              </span>
            </div>
            {blockers.length > 0 && maxPossibleToday < remainingQuota && (
              <div className="mt-2 space-y-1">
                {blockers.map((b, i) => (
                  <div key={i} className="text-[10.5px] text-zinc-500 flex items-start gap-1.5">
                    <span aria-hidden="true">{b.icon}</span>
                    <span><strong className="text-zinc-300">{b.label}</strong> — {b.hint}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}




/**
 * BlacklistTabContent — splits the existing blacklist tab into 2 sub-tabs:
 * 1. "Domaines bloqués" — manual entries from /api/blacklist (source !== self_unsubscribe)
 * 2. "Désinscrits" — emails that clicked the unsubscribe link, fetched from
 *    /api/blacklist/unsubscribed which merges both legacy ai_agent_unsubscribed
 *    and email_blacklist where source=self_unsubscribe.
 */
function BlacklistTabContent({ blacklist, onUnblacklist, onRefresh }) {
  const [subTab, setSubTab] = useState('domains');
  const [unsubs, setUnsubs] = useState([]);
  const [loadingUnsubs, setLoadingUnsubs] = useState(false);

  const manualEntries = (blacklist || []).filter(b => b.source !== 'self_unsubscribe');

  const fetchUnsubs = useCallback(async () => {
    setLoadingUnsubs(true);
    try {
      const { data } = await api.get('/blacklist/unsubscribed');
      setUnsubs(data || []);
    } catch (e) {
      toast.error('Erreur chargement désinscrits');
    } finally {
      setLoadingUnsubs(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'unsubs') fetchUnsubs();
  }, [subTab, fetchUnsubs]);

  const reactivate = async (email) => {
    if (!window.confirm(`Réactiver ${email} ?\nL'agent IA pourra à nouveau le contacter.`)) return;
    try {
      const b64 = btoa(email).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      await api.delete(`/blacklist/unsubscribed/${b64}`);
      toast.success('Email réactivé');
      fetchUnsubs();
      onRefresh?.();
    } catch (e) {
      toast.error('Erreur réactivation');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 border-b border-zinc-800">
        <button
          onClick={() => setSubTab('domains')}
          data-testid="subtab-domains"
          className={`px-3 py-2 text-xs font-medium rounded-t-sm transition-colors ${
            subTab === 'domains'
              ? 'text-rose-400 border-b-2 border-rose-400 -mb-px'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          Domaines bloqués ({manualEntries.length})
        </button>
        <button
          onClick={() => setSubTab('unsubs')}
          data-testid="subtab-unsubs"
          className={`px-3 py-2 text-xs font-medium rounded-t-sm transition-colors ${
            subTab === 'unsubs'
              ? 'text-amber-400 border-b-2 border-amber-400 -mb-px'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          Désinscrits ({unsubs.length || '…'})
        </button>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        {subTab === 'domains' ? (
          manualEntries.length === 0 ? (
            <CardContent className="text-center py-16 text-zinc-500">
              Aucun domaine ou email bloqué manuellement.
              <br />
              <span className="text-zinc-600 text-xs mt-2 inline-block">
                Utilisez le bouton 🚫 sur une fiche prospect pour bloquer un email.
              </span>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-800">
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="text-left p-3">Email / Domaine</th>
                    <th className="text-left p-3">Raison</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {manualEntries.map(b => (
                    <tr key={b.id} className="border-b border-zinc-800/50" data-testid={`blacklist-row-${b.id}`}>
                      <td className="p-3 text-zinc-200 font-mono text-xs">{b.email}</td>
                      <td className="p-3 text-zinc-400 text-xs">{b.reason || '—'}</td>
                      <td className="p-3 text-zinc-500 text-[10px]">{new Date(b.created_at).toLocaleDateString('fr-FR')}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => onUnblacklist(b.id)} className="p-1 text-zinc-500 hover:text-emerald-400" title="Retirer" data-testid={`unblacklist-${b.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <>
            {loadingUnsubs ? (
              <CardContent className="text-center py-16 text-zinc-500">Chargement…</CardContent>
            ) : unsubs.length === 0 ? (
              <CardContent className="text-center py-16 text-zinc-500">
                Aucun email désinscrit.
                <br />
                <span className="text-zinc-600 text-xs mt-2 inline-block">
                  Les emails s'ajoutent automatiquement quand un destinataire clique « Se désinscrire » dans un mail.
                </span>
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="unsubs-table">
                  <thead className="border-b border-zinc-800">
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Raison</th>
                      <th className="text-left p-3">Source</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-right p-3">Réactiver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsubs.map((u, i) => (
                      <tr key={u.email + i} className="border-b border-zinc-800/50" data-testid={`unsub-row-${u.email}`}>
                        <td className="p-3 text-zinc-200 font-mono text-xs">{u.email}</td>
                        <td className="p-3 text-zinc-400 text-xs">{u.reason || 'Clic « se désinscrire »'}</td>
                        <td className="p-3 text-[10px]">
                          <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300">
                            {u.source === 'email_blacklist' ? 'Lien email' : 'Legacy'}
                          </span>
                        </td>
                        <td className="p-3 text-zinc-500 text-[10px]">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => reactivate(u.email)}
                            className="p-1 text-zinc-500 hover:text-emerald-400"
                            title="Réactiver — l'agent pourra à nouveau contacter cet email"
                            data-testid={`reactivate-${u.email}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
